/**
 * PrivacyStep - Third step of the onboarding flow
 *
 * Privacy-first messaging to build trust. Explains how user data
 * is handled: no storage of conversations, on-device processing,
 * and anonymized analytics.
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTranslation } from '@dramadetector/shared';

export const PrivacyStep: React.FC = () => {
  const { t } = useTranslation('common');

  const PRIVACY_POINTS = [
    {
      icon: '🔒',
      title: t('privacy_private_title', { defaultValue: 'Conversations stay private' }),
      description: t('privacy_private_desc', { defaultValue: 'Your messages are analyzed and discarded. We never store conversation content.' }),
    },
    {
      icon: '🛡️',
      title: t('privacy_no_selling_title', { defaultValue: 'No data selling' }),
      description: t('privacy_no_selling_desc', { defaultValue: 'Your data is never sold to third parties. Period.' }),
    },
    {
      icon: '📊',
      title: t('privacy_anonymous_title', { defaultValue: 'Anonymous analytics only' }),
      description: t('privacy_anonymous_desc', { defaultValue: 'Usage analytics are fully anonymized. We track patterns, not people.' }),
    },
    {
      icon: '🗑️',
      title: t('privacy_delete_title', { defaultValue: 'Delete anytime' }),
      description: t('privacy_delete_desc', { defaultValue: 'Your account and all associated data can be deleted instantly from Settings.' }),
    },
  ];

  return (
    <View style={styles.container}>
      <Text style={styles.shieldEmoji}>{'🛡️'}</Text>
      <Text style={styles.title}>{t('privacy_title', { defaultValue: 'Your Privacy Matters' })}</Text>
      <Text style={styles.subtitle}>
        {t('privacy_subtitle', { defaultValue: 'Built with privacy at the core' })}
      </Text>
      <View style={styles.pointsContainer}>
        {PRIVACY_POINTS.map((point, index) => (
          <View key={index} style={styles.pointRow}>
            <Text style={styles.pointIcon}>{point.icon}</Text>
            <View style={styles.pointContent}>
              <Text style={styles.pointTitle}>{point.title}</Text>
              <Text style={styles.pointDescription}>{point.description}</Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 16,
  },
  shieldEmoji: {
    fontSize: 56,
    marginBottom: 12,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: '#1a1a2e',
    textAlign: 'center',
    marginBottom: 8,
    fontFamily: 'PlusJakartaSans_700Bold',
  },
  subtitle: {
    fontSize: 16,
    color: '#6b7280',
    textAlign: 'center',
    marginBottom: 28,
    fontFamily: 'Inter_400Regular',
  },
  pointsContainer: {
    width: '100%',
    gap: 16,
  },
  pointRow: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-start',
  },
  pointIcon: {
    fontSize: 22,
    width: 32,
    textAlign: 'center',
    marginTop: 2,
  },
  pointContent: {
    flex: 1,
  },
  pointTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#1a1a2e',
    marginBottom: 2,
    fontFamily: 'Inter_600SemiBold',
  },
  pointDescription: {
    fontSize: 13,
    color: '#6b7280',
    lineHeight: 18,
    fontFamily: 'Inter_400Regular',
  },
});
